/**
 * Heat Pump Simulation Component
 * Interactive simulation of a heat pump heating system
 * @module HeatPumpSimulation
 */

import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PagesWrapper } from "../../../PagesWrapper";
import { useTrainingProfile } from "../../../hooks/useTrainingProfile";

// Constants and utilities
import { ROOM_WIDTH, ROOM_HEIGHT, RADIATOR_POWER_MATRIX } from "./constants";

// Custom hooks
import { useHeatPumpSimulation, useWeatherData } from "./hooks";

// Components
import { RoomUnit } from "./RoomUnit";
import { ExternalUnit } from "./ExternalUnit";
import { ControlPanel } from "./ControlPanel";
import { SimulationToolbar } from "./Toolbar";
import { RadiatorRatingOverlay, HeatFluxOverlay } from "./VisualizationOverlays";
import { UParametersModal, RadiatorTypesModal, DocumentResourcesModal, BuildingEraModal, MapLocationModal } from "./Modals";
import { HeatingCurveModal, calculateOptimalSupplyTemperature } from "./HeatingCurve";
import { EnergyFlowDrawer } from "./EnergyFlowDrawer";
import { TrainingProfileSelector } from "./TrainingProfileSelector";
import { TutorialQuests } from "./TutorialQuests";
import { getProfileFixedValue, isProfileControlHidden, isProfileValueLocked } from "./trainingProfileUtils";
import styles from './HeatPumpSimulation.module.css';

const ROOM_TYPE_TARGET_TEMPERATURES = {
  living: 20,
  bathroom: 24,
};

const getTemperatureForRoomType = (roomType) => ROOM_TYPE_TARGET_TEMPERATURES[roomType] ?? null;

const getRoomTypeForTemperature = (temperature) => {
  const numericTemperature = Number(temperature);
  return Object.entries(ROOM_TYPE_TARGET_TEMPERATURES).find(([, target]) => target === numericTemperature)?.[0] ?? null;
};

const hasOwnProfileValue = (source, key) => Object.prototype.hasOwnProperty.call(source ?? {}, key);

/**
 * Main Heat Pump Simulation Component
 */
export function HeatPumpSimulation({ trainingProfileMode = false, trainingProfileAccessMode = 'public' }) {
  const { t } = useTranslation();
  const showTrainingProfileSelector = trainingProfileMode && trainingProfileAccessMode !== 'preview';
  
  // ===== State Management =====

  // Outdoor conditions
  const [outdoorTemperature, setOutdoorTemperature] = useState(5);
  const [gpsPosition, setGpsPosition] = useState([50.77664, 6.08342]); // Default: Aachen
  const [selectedCity, setSelectedCity] = useState(null);

  // System parameters
  const [supplyTemperature, setSupplyTemperature] = useState(55);
  const [systemPressure, setSystemPressure] = useState(0.1);
  const [radiatorCount, setRadiatorCount] = useState(5);
  const [radiatorType, setRadiatorType] = useState("22");

  // Room conditions
  const [desiredTemperature, setDesiredTemperature] = useState(20);
  const [currentTemperature, setCurrentTemperature] = useState(19);
  const [temperatureChange, setTemperatureChange] = useState(0);
  const [selectedRoomType, setSelectedRoomType] = useState('living');

  // Valve and thermostat
  const [valvePosition, setValvePosition] = useState(3 / 7); // 0 to 1
  const [thermostatLevel, setThermostatLevel] = useState(1); // 0 to 1

  // Building envelope
  const [envelopeUValue, setEnvelopeUValue] = useState(0.35);
  const [totalArea, setTotalArea] = useState(60);
  const [roofArea, setRoofArea] = useState(10);
  const [roofU, setRoofU] = useState(0.25);
  const [wallArea, setWallArea] = useState(45);
  const [wallU, setWallU] = useState(0.3);
  const [windowArea, setWindowArea] = useState(5);
  const [windowU, setWindowU] = useState(1);
  const [airChangeRate, setAirChangeRate] = useState(0.5); // Air changes per hour (ACH)

  // System state
  const [massFlowRate, setMassFlowRate] = useState(0);
  const [heatOutput, setHeatOutput] = useState(0);
  const [returnTemperature, setReturnTemperature] = useState(0);
  const [envelopeLoss, setEnvelopeLoss] = useState(0);
  const [ventilationLoss, setVentilationLoss] = useState(0);
  const [heatLossBreakdown, setHeatLossBreakdown] = useState({ west: 0, east: 0, north: 0, south: 0 });

  // UI state
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showRadiatorRating, setShowRadiatorRating] = useState(false);
  const [showHeatFlux, setShowHeatFlux] = useState(false);
  const [showUParameters, setShowUParameters] = useState(false);
  const [showRadiatorTypes, setShowRadiatorTypes] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [showBuildingEra, setShowBuildingEra] = useState(false);
  const [showMapLocation, setShowMapLocation] = useState(false);
  const [showHeatingCurve, setShowHeatingCurve] = useState(false);
  const [showEnergyFlow, setShowEnergyFlow] = useState(false);
  const [autoWeatherCompensation, setAutoWeatherCompensation] = useState(false);
  const [showHighlights, setShowHighlights] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [dockedPanelTarget, setDockedPanelTarget] = useState(null);

  // Refs and hooks
  const [searchParams, setSearchParams] = useSearchParams();
  const showHeaderInPage = searchParams.get("showHeader");
  const profileIdFromUrl = searchParams.get("profileId");

  const trainingProfileState = useTrainingProfile({
    enabled: trainingProfileMode,
    initialProfileId: profileIdFromUrl,
    accessMode: trainingProfileAccessMode,
  });
  const activeTrainingProfile = trainingProfileMode ? trainingProfileState.selectedProfile : null;

  const isValueLocked = useCallback(
    (key) => isProfileValueLocked(activeTrainingProfile, key),
    [activeTrainingProfile]
  );

  const isExactProfileValueLocked = useCallback((key) => {
    if (!activeTrainingProfile || !key) {
      return false;
    }

    const fixedValues = activeTrainingProfile.fixedValues || {};
    const fixedValue = hasOwnProfileValue(fixedValues, key) ? fixedValues[key] : undefined;
    const controls = activeTrainingProfile.uiConfiguration?.controls || activeTrainingProfile.uiConfiguration || {};
    const control = controls[key] || controls[`controlPanel.${key}`];

    if (fixedValue !== undefined && fixedValue !== null) {
      if (typeof fixedValue === "object") {
        return fixedValue.locked !== false && fixedValue.editable !== true;
      }

      return true;
    }

    return control?.locked === true || control?.editable === false;
  }, [activeTrainingProfile]);

  const isRadiatorTypeLocked = useCallback(
    () => isExactProfileValueLocked("radiatorType"),
    [isExactProfileValueLocked]
  );

  const isRadiatorSizeLocked = useCallback(
    () => isExactProfileValueLocked("radiatorSize") || isExactProfileValueLocked("radiatorCount"),
    [isExactProfileValueLocked]
  );

  const isControlHidden = useCallback(
    (key) => isProfileControlHidden(activeTrainingProfile, key),
    [activeTrainingProfile]
  );

  const showRoomValveThermostat = !isControlHidden("roomUnit.valveThermostat");
  const showRoomRadiatorArea = !isControlHidden("roomUnit.radiatorArea");
  const showExternalUnit = !isControlHidden("visualization.externalUnit");
  const showRadiatorRatingTool = !isControlHidden("toolbar.radiatorRatingOverlay");
  const showHeatFluxTool = !isControlHidden("toolbar.heatFluxOverlay");

  const createProfileAwareSetter = useCallback(
    (key, setter) => (value) => {
      if (!isValueLocked(key)) {
        setter(value);
      }
    },
    [isValueLocked]
  );

  const setOutdoorTemperatureFromWeather = useCallback((value) => {
    if (!isValueLocked("outdoorTemperature")) {
      setOutdoorTemperature(value);
    }
  }, [isValueLocked]);

  const handleTrainingProfileChange = useCallback((profileId) => {
    trainingProfileState.setSelectedProfileId(profileId);
    const nextParams = new URLSearchParams(searchParams);
    if (profileId) {
      nextParams.set("profileId", profileId);
    } else {
      nextParams.delete("profileId");
    }
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams, trainingProfileState]);

  // ===== Custom Hooks =====

  // Weather data fetching
  useWeatherData(gpsPosition, setOutdoorTemperatureFromWeather);

  // Main simulation logic (replaces manual timer and calculation logic)
  const { cop: currentCOP } = useHeatPumpSimulation(
    {
      // System parameters
      outsideTemperature: outdoorTemperature,
      supplyTemperature,
      systemPressure,
      pipeLength: 5, // Default pipe length
      pipeDiameter: 15, // Default pipe diameter

      // Room parameters
      roomTemperature: currentTemperature,
      targetTemperature: desiredTemperature,
      uValue: envelopeUValue,
      totalArea,
      roofArea,
      wallArea,
      airChangeRate,

      // Radiator parameters
      radiatorCount,
      radiatorType,
      valvePosition,
      thermostatOpening: thermostatLevel,

      // Control parameters
      isAnimationRunning: isSimulationRunning,
      isAnimationPaused: isPaused,
    },
    {
      // State setters
      setRoomTemperature: setCurrentTemperature,
      setThermostatOpening: setThermostatLevel,
      setReturnTemperature,
      setMassFlow: setMassFlowRate,
      setHeatOutput,
      setEnvelopeLoss,
      setVentilationLoss,
      setHeatLossBreakdown,
      setTemperatureChange,
    }
  );

  // ===== Effects =====

  // Header visibility from URL parameter
  useEffect(() => {
    setHeaderVisible(!Object.is(showHeaderInPage, "false"));
  }, [showHeaderInPage]);

  useEffect(() => {
    if (!showRadiatorRatingTool && showRadiatorRating) {
      setShowRadiatorRating(false);
    }
  }, [showRadiatorRating, showRadiatorRatingTool]);

  useEffect(() => {
    if (!showHeatFluxTool && showHeatFlux) {
      setShowHeatFlux(false);
    }
  }, [showHeatFlux, showHeatFluxTool]);

  useEffect(() => {
    if (!showRoomValveThermostat && showHighlights) {
      setShowHighlights(false);
    }
  }, [showHighlights, showRoomValveThermostat]);

  // Apply profile defaults and fixed values when a learner selects a profile.
  useEffect(() => {
    if (!trainingProfileMode || !activeTrainingProfile) return;

    const applyValue = (key, value) => {
      if (value === undefined || value === null) return;

      switch (key) {
        case "outdoorTemperature": setOutdoorTemperature(Number(value)); break;
        case "supplyTemperature": setSupplyTemperature(Number(value)); break;
        case "systemPressure": setSystemPressure(Number(value)); break;
        case "radiatorCount": setRadiatorCount(Number(value)); break;
        case "radiatorSize": setRadiatorCount(Number(value) / 10); break;
        case "radiatorType": setRadiatorType(String(value)); break;
        case "desiredTemperature": setDesiredTemperature(Number(value)); break;
        case "currentTemperature": setCurrentTemperature(Number(value)); break;
        case "selectedRoomType": setSelectedRoomType(value ? String(value) : null); break;
        case "valvePosition": setValvePosition(Number(value)); break;
        case "thermostatLevel": setThermostatLevel(Number(value)); break;
        case "uValue":
        case "envelopeUValue": setEnvelopeUValue(Number(value)); break;
        case "totalArea": setTotalArea(Number(value)); break;
        case "roofArea": setRoofArea(Number(value)); break;
        case "roofU": setRoofU(Number(value)); break;
        case "wallArea": setWallArea(Number(value)); break;
        case "wallU": setWallU(Number(value)); break;
        case "windowArea": setWindowArea(Number(value)); break;
        case "windowU": setWindowU(Number(value)); break;
        case "airChangeRate": setAirChangeRate(Number(value)); break;
        case "selectedCity": setSelectedCity(value ? String(value) : null); break;
        case "autoWeatherCompensation": setAutoWeatherCompensation(Boolean(value)); break;
        default: break;
      }
    };

    const mergedValues = {
      ...(activeTrainingProfile.simulationDefaults || {}),
    };

    Object.keys(activeTrainingProfile.fixedValues || {}).forEach((key) => {
      mergedValues[key] = getProfileFixedValue(activeTrainingProfile, key);
    });

    if (mergedValues.selectedRoomType !== undefined && mergedValues.desiredTemperature === undefined) {
      const targetTemperature = getTemperatureForRoomType(String(mergedValues.selectedRoomType));
      if (targetTemperature !== null) {
        mergedValues.desiredTemperature = targetTemperature;
      }
    }

    if (mergedValues.desiredTemperature !== undefined) {
      mergedValues.selectedRoomType = getRoomTypeForTemperature(mergedValues.desiredTemperature);
    }

    Object.entries(mergedValues).forEach(([key, value]) => applyValue(key, value));
  }, [trainingProfileMode, activeTrainingProfile]);

  useEffect(() => {
    const matchedRoomType = getRoomTypeForTemperature(desiredTemperature);
    setSelectedRoomType((currentRoomType) => (currentRoomType === matchedRoomType ? currentRoomType : matchedRoomType));
  }, [desiredTemperature]);

  // Ensure total area doesn't become zero
  useEffect(() => {
    if (totalArea === 0) {
      setTotalArea(7);
    }
  }, [totalArea]);

  // Update total area as sum of roof, wall, and window areas
  useEffect(() => {
    const calculatedTotalArea = roofArea + wallArea + windowArea;
    setTotalArea(calculatedTotalArea > 0 ? calculatedTotalArea : 7);
  }, [roofArea, wallArea, windowArea]);

  // Calculate envelope U-value as weighted average of component U-values
  useEffect(() => {
    const totalComponentArea = roofArea + wallArea + windowArea;
    if (totalComponentArea > 0) {
      const weightedUValue = (roofArea * roofU + wallArea * wallU + windowArea * windowU) / totalComponentArea;
      setEnvelopeUValue(weightedUValue);
    }
  }, [roofArea, roofU, wallArea, wallU, windowArea, windowU]);

  // Continuous weather compensation: automatically adjust supply temperature
  // This runs continuously when enabled to reach and maintain desired temperature
  useEffect(() => {
    if (!autoWeatherCompensation || isValueLocked("supplyTemperature")) return;

    // Calculate optimal supply temperature based on current conditions
    const optimal = calculateOptimalSupplyTemperature({
      currentRoomTemperature: currentTemperature,
      targetTemperature: desiredTemperature,
      outdoorTemperature,
      uValue: envelopeUValue,
      totalArea,
      roofArea,
      wallArea,
      airChangeRate,
      radiatorCount,
      radiatorType,
      valvePosition,
      systemPressure,
    });

    // Only update if there's a meaningful difference (avoid jitter)
    if (Math.abs(optimal - supplyTemperature) >= 1) {
      setSupplyTemperature(optimal);
    }
  }, [
    autoWeatherCompensation,
    currentTemperature,
    desiredTemperature,
    outdoorTemperature,
    envelopeUValue,
    totalArea,
    roofArea,
    wallArea,
    airChangeRate,
    radiatorCount,
    radiatorType,
    valvePosition,
    systemPressure,
    supplyTemperature,
    isValueLocked,
  ]);


  // ===== Handler Functions =====

  const handlePlayPause = useCallback(() => {
    setIsSimulationRunning((prev) => !prev);
    setIsPaused(false);
  }, []);

  const handleRoomTypeChange = useCallback((type) => {
    if (isValueLocked("selectedRoomType")) return;

    setSelectedRoomType(type);
    if (isValueLocked("desiredTemperature")) return;

    const targetTemperature = getTemperatureForRoomType(type);
    if (targetTemperature !== null) {
      setDesiredTemperature(targetTemperature);
    }
  }, [isValueLocked]);

  const handleOpenEnergyFlow = useCallback(() => {
    setShowEnergyFlow(true);
  }, []);

  // Calculate combined valve state and nominal power for display
  const combinedValveState = valvePosition * thermostatLevel;
  const isHeatPumpRunning = isSimulationRunning && combinedValveState > 0;
  const radiatorLength = radiatorCount * 100;
  const nominalPower = (RADIATOR_POWER_MATRIX[radiatorLength]?.[radiatorType] || 0) / 1000; // kW
  const rValue = totalArea > 0 ? 1 / (totalArea * envelopeUValue) : 0;
  const deliveredHeatingPower = isHeatPumpRunning ? Math.max(heatOutput, 0) : 0;
  const electricalInputPower = currentCOP > 0 ? deliveredHeatingPower / currentCOP : 0;
  const environmentalHeatPower = Math.max(deliveredHeatingPower - electricalInputPower, 0);

  // ===== Render =====

  const tutorialProps = {
    trainingProfile: activeTrainingProfile,
    trainingProfileMode,
    isSimulationRunning,
    outdoorTemperature,
    desiredTemperature,
    selectedRoomType,
    selectedCity,
    roofArea,
    wallArea,
    windowArea,
    uValue: envelopeUValue,
    totalArea,
    airChangeRate,
    supplyTemperature,
    heatOutput,
    envelopeLoss,
    ventilationLoss,
    valvePosition,
    thermostatLevel,
    currentCOP,
    currentTemperature,
    radiatorType,
    radiatorCount,
    systemPressure,
  };

  const controlPanelContent = (
    <ControlPanel
      // Temperature parameters
      outsideTemperature={outdoorTemperature}
      onOutsideTemperatureChange={createProfileAwareSetter("outdoorTemperature", setOutdoorTemperature)}
      targetTemperature={desiredTemperature}
      onTargetTemperatureChange={createProfileAwareSetter("desiredTemperature", setDesiredTemperature)}
      supplyTemperature={supplyTemperature}
      onSupplyTemperatureChange={createProfileAwareSetter("supplyTemperature", setSupplyTemperature)}
      // Building parameters
      uValue={envelopeUValue}
      onUValueChange={createProfileAwareSetter("uValue", setEnvelopeUValue)}
      totalArea={totalArea}
      onTotalAreaChange={createProfileAwareSetter("totalArea", setTotalArea)}
      rValue={rValue}
      roofArea={roofArea}
      wallArea={wallArea}
      envelopeLoss={envelopeLoss}
      ventilationLoss={ventilationLoss}
      // Radiator parameters
      radiatorType={radiatorType}
      onRadiatorTypeChange={(nextRadiatorType) => {
        if (!isRadiatorTypeLocked()) {
          setRadiatorType(nextRadiatorType);
        }
      }}
      radiatorSize={radiatorCount * 10}
      onRadiatorSizeChange={(size) => {
        if (!isRadiatorSizeLocked()) {
          setRadiatorCount(size / 10);
        }
      }}
      radiatorTypeLocked={isRadiatorTypeLocked()}
      radiatorSizeLocked={isRadiatorSizeLocked()}
      nominalPower={nominalPower}
      qrad={heatOutput}
      valvePosition={valvePosition}
      thermostatOpening={thermostatLevel}
      // System parameters
      systemPressure={systemPressure}
      onSystemPressureChange={createProfileAwareSetter("systemPressure", setSystemPressure)}
      airChangeRate={airChangeRate}
      onAirChangeRateChange={createProfileAwareSetter("airChangeRate", setAirChangeRate)}
      // Callbacks
      onShowUParametersClick={() => setShowUParameters(true)}
      onShowRadiatorTypesClick={() => setShowRadiatorTypes(true)}
      onShowBuildingEraClick={() => setShowBuildingEra(true)}
      onShowMapLocationClick={() => setShowMapLocation(true)}
      onShowHeatingCurveClick={() => setShowHeatingCurve(true)}
      // Weather compensation
      autoWeatherCompensation={autoWeatherCompensation}
      onAutoWeatherCompensationChange={setAutoWeatherCompensation}
      currentRoomTemperature={currentTemperature}
      // Selection state
      selectedCity={selectedCity}
      onSelectedCityChange={createProfileAwareSetter("selectedCity", setSelectedCity)}
      selectedRoomType={selectedRoomType}
      onSelectedRoomTypeChange={handleRoomTypeChange}
      profileControlState={{
        isLocked: isValueLocked,
        isHidden: isControlHidden,
      }}
    />
  );

  return (
    <PagesWrapper showHeader={headerVisible}>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
        {/* Header */}
        <div className={styles.pageHeaderRow}>
          <div className={styles.pageHeaderLeft}>
            <p className={styles.pageTitle}>{t('heatPumpSimulation.title')}</p>
          </div>

          <div className={styles.pageHeaderRight}>
            {showTrainingProfileSelector && (
              <TrainingProfileSelector
                profiles={trainingProfileState.profiles}
                selectedProfileId={trainingProfileState.selectedProfileId}
                selectedProfile={activeTrainingProfile}
                loading={trainingProfileState.loading}
                error={trainingProfileState.error}
                onProfileChange={handleTrainingProfileChange}
              />
            )}
          </div>
        </div>

        {/* Toolbar */}
        <SimulationToolbar
          isPlaying={isSimulationRunning}
          onPlayPause={handlePlayPause}
          showRadiatorRating={showRadiatorRating && showRadiatorRatingTool}
          onToggleRadiatorRating={() => {
            setShowRadiatorRating(!showRadiatorRating);
            setShowHeatFlux(false);
          }}
          showRadiatorRatingTool={showRadiatorRatingTool}
          showHeatFlux={showHeatFlux && showHeatFluxTool}
          onToggleHeatFlux={() => {
            setShowHeatFlux(!showHeatFlux);
            setShowRadiatorRating(false);
          }}
          showHeatFluxTool={showHeatFluxTool}
          onOpenResources={() => setShowResources(true)}
          headerVisible={headerVisible}
          onToggleHeader={() => setHeaderVisible(!headerVisible)}
          showResourcesButton
          showHeaderToggle
          tutorialProps={trainingProfileMode
            ? {
                ...tutorialProps,
                layoutMode: 'docked',
                controlsContent: controlPanelContent,
                dockedPanelTarget,
              }
            : tutorialProps}
        />

        {/* Main Content */}
        <div className={styles.mainContentRow}>
          {/* Visualization */}
          <svg
            width={700}
            height={550}
            className={styles.simulationCanvas}
            style={{ border: "1px solid #d9d9d9", borderRadius: "4px" }}
            onMouseLeave={() => {
              setShowHighlights(false);
              setIsPaused(false);
            }}
          >
            {/* External Unit */}
            {showExternalUnit && (
              <ExternalUnit
                outdoorTemperature={outdoorTemperature}
                supplyTemperature={supplyTemperature}
                returnTemperature={returnTemperature}
                systemPressure={systemPressure}
                cop={currentCOP}
                isRunning={isHeatPumpRunning}
                onOpenEnergyFlow={handleOpenEnergyFlow}
              />
            )}

            {/* Room */}
            <RoomUnit
              x={100}
              y={480 - ROOM_HEIGHT}
              roomTemperature={currentTemperature}
              targetTemperature={desiredTemperature}
              radiatorCount={radiatorCount}
              valvePosition={valvePosition}
              thermostatOpening={thermostatLevel}
              massFlowRate={massFlowRate}
              showHighlights={showHighlights}
              isRunning={isSimulationRunning}
              showValveThermostat={showRoomValveThermostat}
              showRadiatorArea={showRoomRadiatorArea}
              onShowHighlights={setShowHighlights}
              onStopAnimation={setIsPaused}
              onValveChange={createProfileAwareSetter("valvePosition", setValvePosition)}
              onTemperatureChange={createProfileAwareSetter("desiredTemperature", setDesiredTemperature)}
            />

            {/* House Structure — Realistic Building Walls with 3D Effect */}
            <svg>
              <defs>
                <linearGradient id="wallGradL" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#B5A08A" />
                  <stop offset="100%" stopColor="#C8B89C" />
                </linearGradient>
                <linearGradient id="wallGradR" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#C8B89C" />
                  <stop offset="100%" stopColor="#B5A08A" />
                </linearGradient>
                <linearGradient id="ceilGrad" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#C8B89C" />
                  <stop offset="100%" stopColor="#AE9E88" />
                </linearGradient>
                <linearGradient id="side3D" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#9E8E78" />
                  <stop offset="100%" stopColor="#887868" />
                </linearGradient>
                <linearGradient id="roof3D" x1="0" y1="1" x2="0.3" y2="0">
                  <stop offset="0%" stopColor="#8E7E6E" />
                  <stop offset="100%" stopColor="#7A6A5A" />
                </linearGradient>
                <linearGradient id="floorGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B7355" />
                  <stop offset="100%" stopColor="#73614D" />
                </linearGradient>
              </defs>

              <g id="building-structure">
                {/* Floor base */}
                {/* <rect x={100} y={476} width={ROOM_WIDTH} height={4} fill="url(#floorGrad)" /> */}

                {/* Left wall (thick, plaster color) */}
                <rect x={100} y={480 - ROOM_HEIGHT} width={10} height={ROOM_HEIGHT} fill="url(#wallGradL)" />
                <line x1={110} x2={110} y1={480 - ROOM_HEIGHT + 10} y2={476} stroke="#D8CCBA" strokeWidth={0.5} opacity={0.6} />

                {/* Right wall (thick, plaster color) */}
                <rect x={100 + ROOM_WIDTH - 10} y={480 - ROOM_HEIGHT} width={10} height={ROOM_HEIGHT} fill="url(#wallGradR)" />
                <line x1={100 + ROOM_WIDTH - 10} x2={100 + ROOM_WIDTH - 10} y1={480 - ROOM_HEIGHT + 10} y2={476} stroke="#D8CCBA" strokeWidth={0.5} opacity={0.6} />

                {/* Ceiling (thick) */}
                <rect x={100} y={480 - ROOM_HEIGHT} width={ROOM_WIDTH} height={10} fill="url(#ceilGrad)" />
                <line x1={110} x2={100 + ROOM_WIDTH - 10} y1={480 - ROOM_HEIGHT + 10} y2={480 - ROOM_HEIGHT + 10} stroke="#D8CCBA" strokeWidth={0.5} opacity={0.5} />

                {/* Interior baseboard */}
                {/*<rect x={110} y={473} width={ROOM_WIDTH - 20} height={3} fill="#6B5B47" rx={0.5} opacity={0.6} />*/}

                {/* Interior crown molding hint */}
                {/* <rect x={110} y={480 - ROOM_HEIGHT + 10} width={ROOM_WIDTH - 20} height={2} fill="#D8CCBA" rx={0.5} opacity={0.4} /> */}

                {/* === 3D Right Side Wall (perspective depth) === */}
                <polygon
                  points={`${100 + ROOM_WIDTH},${480 - ROOM_HEIGHT} ${100 + ROOM_WIDTH + 20},${480 - ROOM_HEIGHT - 15} ${100 + ROOM_WIDTH + 20},${480 - 15} ${100 + ROOM_WIDTH},${480}`}
                  fill="url(#side3D)" stroke="#7A6C58" strokeWidth={0.8}
                />
                {/* Subtle mortar / siding lines on side wall */}
                {[0.25, 0.5, 0.75].map((f) => (
                  <line key={`mortar-${f}`}
                    x1={100 + ROOM_WIDTH} y1={(480 - ROOM_HEIGHT) + ROOM_HEIGHT * f}
                    x2={100 + ROOM_WIDTH + 20} y2={(480 - ROOM_HEIGHT - 15) + ROOM_HEIGHT * f}
                    stroke="#7A6C58" strokeWidth={0.3} opacity={0.5}
                  />
                ))}

                {/* Window on 3D side wall (perspective-matched parallelogram) */}
                {/* Side wall skew: -0.75 y per 1 x from wall edge */}
                <polygon
                  points={`${100 + ROOM_WIDTH + 4},${480 - ROOM_HEIGHT + 50 - 3} ${100 + ROOM_WIDTH + 16},${480 - ROOM_HEIGHT + 50 - 12} ${100 + ROOM_WIDTH + 16},${480 - ROOM_HEIGHT + 135 - 12} ${100 + ROOM_WIDTH + 4},${480 - ROOM_HEIGHT + 135 - 3}`}
                  fill="#87CEEB" fillOpacity={0.3}
                  stroke="#6B5B47" strokeWidth={0.8}
                />
                {/* Window cross-frame — vertical (at center dx=10, shift=-7.5) */}
                <line
                  x1={100 + ROOM_WIDTH + 10} x2={100 + ROOM_WIDTH + 10}
                  y1={480 - ROOM_HEIGHT + 50 - 7.5} y2={480 - ROOM_HEIGHT + 135 - 7.5}
                  stroke="#6B5B47" strokeWidth={0.5}
                />
                {/* Window cross-frame — horizontal (perspective-skewed) */}
                <line
                  x1={100 + ROOM_WIDTH + 4} x2={100 + ROOM_WIDTH + 16}
                  y1={480 - ROOM_HEIGHT + 92 - 3} y2={480 - ROOM_HEIGHT + 92 - 12}
                  stroke="#6B5B47" strokeWidth={0.5}
                />
                

                {/* === 3D Roof / Ceiling Top === */}
                <polygon
                  points={`${100},${480 - ROOM_HEIGHT} ${100 + 20},${480 - ROOM_HEIGHT - 15} ${100 + ROOM_WIDTH + 20},${480 - ROOM_HEIGHT - 15} ${100 + ROOM_WIDTH},${480 - ROOM_HEIGHT}`}
                  fill="url(#roof3D)" stroke="#7A6C58" strokeWidth={0.8}
                />
                {/* Roof top edge */}
                <line
                  x1={100 + 20} x2={100 + ROOM_WIDTH + 20}
                  y1={480 - ROOM_HEIGHT - 15} y2={480 - ROOM_HEIGHT - 15}
                  stroke="#5C4E3C" strokeWidth={1.2}
                />

                {/* Small chimney */}
                <rect
                  x={100 + ROOM_WIDTH - 30} y={480 - ROOM_HEIGHT - 30}
                  width={14} height={16}
                  fill="#8B7D68" stroke="#5C4E3C" strokeWidth={0.8} rx={1}
                />
                <rect
                  x={100 + ROOM_WIDTH - 32} y={480 - ROOM_HEIGHT - 33}
                  width={18} height={4}
                  fill="#7A6C58" stroke="#5C4E3C" strokeWidth={0.5} rx={1}
                />

                {/* Outer frame edges */}
                <line x1={100} x2={100} y1={480 - ROOM_HEIGHT} y2={480} stroke="#7A6C58" strokeWidth={1.5} />
                <line x1={100 + ROOM_WIDTH} x2={100 + ROOM_WIDTH} y1={480 - ROOM_HEIGHT} y2={480} stroke="#7A6C58" strokeWidth={1.5} />
                <line x1={100} x2={100 + ROOM_WIDTH} y1={480 - ROOM_HEIGHT} y2={480 - ROOM_HEIGHT} stroke="#7A6C58" strokeWidth={1.5} />
                <line x1={100} x2={100 + ROOM_WIDTH} y1={480} y2={480} stroke="#7A6C58" strokeWidth={1} />
              </g>
            </svg>

            {/* Overlays */}
            {showRadiatorRating && showRadiatorRatingTool && (
              <svg x={92} y={480 - ROOM_HEIGHT}>
                <RadiatorRatingOverlay
                  heatOutput={heatOutput}
                  supplyTemperature={supplyTemperature}
                  returnTemperature={returnTemperature}
                  radiatorCount={radiatorCount}
                />
              </svg>
            )}

            {showHeatFlux && showHeatFluxTool && (
              <svg x={100 - 150} y={480 - ROOM_HEIGHT - 150} width={ROOM_WIDTH + 300} height={ROOM_HEIGHT + 300}>
                <HeatFluxOverlay
                  heatOutput={heatOutput}
                  heatLoss={envelopeLoss}
                  ventilationLoss={ventilationLoss}
                  heatLossBreakdown={heatLossBreakdown}
                  roomTemperature={currentTemperature}
                  temperatureChange={temperatureChange}
                />
              </svg>
            )}
          </svg>

          {trainingProfileMode ? (
            <div ref={setDockedPanelTarget} className={styles.trainingProfileSideColumn} />
          ) : controlPanelContent}
        </div>
      </div>

      {/* Modals */}
      <UParametersModal
        visible={showUParameters}
        onClose={() => setShowUParameters(false)}
        roofArea={roofArea}
        roofU={roofU}
        wallArea={wallArea}
        wallU={wallU}
        windowArea={windowArea}
        windowU={windowU}
        onRoofAreaChange={setRoofArea}
        onRoofUChange={setRoofU}
        onWallAreaChange={setWallArea}
        onWallUChange={setWallU}
        onWindowAreaChange={setWindowArea}
        onWindowUChange={setWindowU}
        onEnvelopeUChange={setEnvelopeUValue}
      />

      <RadiatorTypesModal visible={showRadiatorTypes} onClose={() => setShowRadiatorTypes(false)} />

      <BuildingEraModal 
        visible={showBuildingEra} 
        onClose={() => setShowBuildingEra(false)} 
        onSelectUValue={createProfileAwareSetter("uValue", setEnvelopeUValue)}
        currentUValue={envelopeUValue}
      />

      <DocumentResourcesModal
        visible={showResources}
        onClose={() => setShowResources(false)}
        resources={trainingProfileMode && activeTrainingProfile ? activeTrainingProfile.learningResources ?? [] : undefined}
      />

      <MapLocationModal
        visible={showMapLocation}
        onClose={() => setShowMapLocation(false)}
        currentPosition={gpsPosition}
        onLocationSelect={setGpsPosition}
        onTemperatureUpdate={(temp) => {
          if (!isValueLocked("outdoorTemperature")) {
            setOutdoorTemperature(temp);
          }
          setSelectedCity(null);
        }}
      />

      <HeatingCurveModal
        visible={showHeatingCurve}
        onClose={() => setShowHeatingCurve(false)}
        outdoorTemperature={outdoorTemperature}
        targetTemperature={desiredTemperature}
        currentRoomTemperature={currentTemperature}
        supplyTemperature={supplyTemperature}
        onSupplyTemperatureChange={createProfileAwareSetter("supplyTemperature", setSupplyTemperature)}
        uValue={envelopeUValue}
        totalArea={totalArea}
        roofArea={roofArea}
        wallArea={wallArea}
        airChangeRate={airChangeRate}
        radiatorCount={radiatorCount}
        radiatorType={radiatorType}
        valvePosition={valvePosition}
        systemPressure={systemPressure}
        autoWeatherCompensation={autoWeatherCompensation}
        onAutoWeatherCompensationChange={setAutoWeatherCompensation}
      />

      <EnergyFlowDrawer
        visible={showEnergyFlow}
        onClose={() => setShowEnergyFlow(false)}
        onToggleSimulation={handlePlayPause}
        isRunning={isHeatPumpRunning}
        isSimulationRunning={isSimulationRunning}
        cop={currentCOP}
        outdoorTemperature={outdoorTemperature}
        supplyTemperature={supplyTemperature}
        returnTemperature={returnTemperature}
        heatingOutputWatts={deliveredHeatingPower}
        electricalInputWatts={electricalInputPower}
        environmentalHeatWatts={environmentalHeatPower}
      />
    </PagesWrapper>
  );
}
